import { useEffect, useState } from "react";
import {
  applyUpdateAndReload,
  canPrompt,
  getPwaState,
  promptInstall,
  subscribe,
} from "@/services/pwaService";
// canPrompt + promptInstall intentionally not imported — see the disabled
// install banner below.

/**
 * Two tiny banners:
 *  - "Update available" — shown when the service worker has a fresh build
 *    waiting. Tap "Update" to reload onto it.
 *  - "Install PageVoice" — shown when the browser exposed a deferrable
 *    beforeinstallprompt (Chrome/Edge desktop, Android Chrome). On iOS Safari
 *    we render a small hint instead since the browser-driven prompt isn't
 *    available there.
 */
export function PwaBanners() {
  const [, setTick] = useState(0);
  useEffect(() => subscribe(() => setTick((t) => t + 1)), []);

  const s = getPwaState();
  const showUpdate = s.updateReady;
  // "Install PageVoice" + iOS "Add to Home Screen" banners disabled per
  // 2026-06-20 ask — the user already runs the PWA installed and the hint
  // banners are visual noise. The update banner still fires when a new SW
  // lands so future deploys can announce themselves.
  // 2026-10-07 — RE-ENABLED. These were switched off on 2026-06-20 because
  // "the user already runs the PWA installed and the hint banners are
  // visual noise". That stopped being true today: rolling back to 261006-1330
  // left Thomas with no way to create a home-screen icon — "it doesn't say
  // install pv so I can't make ICON, hard time to get back" — and he
  // struggled to reach the app at all.
  //
  // Worth recording that the rollback did not REMOVE this: it has been off
  // in every build since June. The install route was missing before and
  // after, which is why it needs turning on rather than restoring.
  const showInstallPrompt = canPrompt();
  const showIOSHint =
    !s.isStandalone &&
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !/crios|fxios/i.test(navigator.userAgent);

  return (
    <>
      {showUpdate && (
        <div className="pv-accent-bg fixed left-2 right-2 top-2 z-50 flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm shadow-lg">
          <span>A newer version of <b>PageVoice</b> Reader is ready.</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void applyUpdateAndReload()}
              className="rounded border border-current px-2 py-0.5 text-xs font-semibold"
            >
              Update
            </button>
          </div>
        </div>
      )}
      {/* 2026-10-07 — the banners themselves are back, not just the flags.
          Thomas could not create a home-screen icon and could not easily
          reach the app at all. Small, dismissible-by-acting, and out of the
          way of the shelf. */}
      {showInstallPrompt && (
        <div className="pv-accent-bg fixed left-2 right-2 top-2 z-50 flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm shadow-lg">
          <span>Install <b>PageVoice</b> for a home-screen icon.</span>
          <button
            onClick={() => void promptInstall()}
            className="rounded border border-current px-2 py-0.5 text-xs font-semibold"
          >
            Install
          </button>
        </div>
      )}
      {showIOSHint && (
        <div className="pv-surface pv-border pv-fg fixed left-2 right-2 top-2 z-50 rounded-md border px-3 py-2 text-sm">
          To add an icon: tap <b>Share</b>, then <b>Add to Home Screen</b>.
        </div>
      )}
    </>
  );
}
